import React from "react";

import CalendarIcon from "@uui/Calendar";
import ClockIcon from "@uui/Clock";
import DotsVerticalIcon from "@uui/DotsVertical";
import LinkIcon from "@uui/Link01";
import VideoRecorderIcon from "@uui/VideoRecorder";

import Typography from "@mui/material/Typography";

import { BOOKING_WINDOW_DESCRIPTION } from "@app/analytics/Calendar/constants";
import { LABEL_FOR_CONFERENCING_PLATFORM } from "@app/common/constants/conferencing";
import type { SchedulingLink } from "@app/common/types";
import { getDisplayUrl } from "@app/common/utils";
import { pluralizePhrase } from "@app/common/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import CopyButton from "@rui/CopyButton";
import Link from "@rui/Link";
import Menu from "@rui/Menu";
import Stack, { Row } from "@rui/Stack";
import Switch from "@rui/Switch";

import { Constraint, Container, IconButton, MenuItem } from "./styles";

interface SchedulingLinkDetailProps {
  link: SchedulingLink;
  linkUrl: string;
  onDelete?: (linkId: string) => void;
  onEdit?: (link: SchedulingLink) => void;
  onToggleEnabled: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  workingHoursEnabled: boolean;
}

const SchedulingLinkDetail = ({
  link,
  linkUrl,
  onDelete,
  onEdit,
  onToggleEnabled,
  workingHoursEnabled,
}: SchedulingLinkDetailProps) => {
  const { onClick, anchorElement, open, onClose } = useAnchorElement();

  const isDefault = link.kind === "default";

  const handleClickEdit = () => {
    onClose();
    onEdit?.(link);
  };

  const handleClickDelete = () => {
    onClose();
    onDelete?.(link.id);
  };

  return (
    <Container isDefault={isDefault} isEnabled={link.isEnabled}>
      <Row variant="apart" spacing={2}>
        <Row spacing={1}>
          <Switch
            checked={link.isEnabled}
            sx={{ m: 0 }}
            onChange={onToggleEnabled}
          />
          <Stack spacing={0.5}>
            <Typography variant="bodyPrimaryMed">{link.title}</Typography>
            <Row variant="left" spacing={1} flexWrap="wrap">
              <Constraint Icon={ClockIcon}>
                {link.allowedDuration
                  ? pluralizePhrase(link.allowedDuration, "minute")
                  : "Any duration"}
              </Constraint>
              {link.platformPreference !== "all" && (
                <Constraint Icon={VideoRecorderIcon}>
                  {LABEL_FOR_CONFERENCING_PLATFORM[link.platformPreference]}
                </Constraint>
              )}
              {workingHoursEnabled && (
                <Constraint Icon={CalendarIcon}>Working hours</Constraint>
              )}
            </Row>
            {!isDefault && (
              <Constraint>
                Allow recipient to book{" "}
                {BOOKING_WINDOW_DESCRIPTION[link.bookingWindow]}
              </Constraint>
            )}
            <Link
              variant="labelReg"
              href={linkUrl}
              sx={{ overflowWrap: "anywhere" }}
            >
              {getDisplayUrl(linkUrl)}
            </Link>
          </Stack>
        </Row>
        <Row variant="right" spacing={1} alignSelf="flex-start">
          <CopyButton
            copyValue={linkUrl}
            renderIconButton={({ children, onClick }) => (
              <IconButton variant="outlined" onClick={onClick}>
                {children ?? <LinkIcon sx={{ color: "shades.primary.500" }} />}
              </IconButton>
            )}
          />
          {!isDefault && (
            <>
              <IconButton variant="text" onClick={onClick}>
                <DotsVerticalIcon />
              </IconButton>
              <Menu
                open={open}
                anchorEl={anchorElement}
                onClose={onClose}
                sx={{ color: "shades.neutral.500" }}
              >
                <MenuItem onClick={handleClickEdit}>Edit custom link</MenuItem>
                <MenuItem onClick={handleClickDelete}>
                  Delete custom link
                </MenuItem>
              </Menu>
            </>
          )}
        </Row>
      </Row>
    </Container>
  );
};

export default SchedulingLinkDetail;
